.contact-section {
  text-align: left;
  margin: 30px 0;

  &:first-child {
    margin-top: 0;
  }

  h3 {
    color: #2c3e50;
    font-size: 14px;
    margin: 35px 0 20px;
    text-align: left;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:first-child {
      margin-top: 0;
    }
  }

  .contact-info {
    .info-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 10px 0;
      
      i {
        color: #666;
      }
      
      span {
        color: #4a5568;
        font-size: 16px;
      }
    }
  }
}    const hasAddress = computed(() => 
      state.profile?.address_line_1 || state.profile?.city || state.profile?.state,
    );

    const formattedAddress = computed(() => {
      if (!state.profile) return '';
      const parts: string[] = [];
      if (state.profile.address_line_1) parts.push(state.profile.address_line_1);
      if (state.profile.address_line_2) parts.push(state.profile.address_line_2);
      if (state.profile.city) parts.push(state.profile.city);
      if (state.profile.state) parts.push(state.profile.state);
      if (state.profile.zipcode) parts.push(state.profile.zipcode);
      return parts.join(', ');
    });<template>
  <web-app-layout>
    <!-- Logo at top of content -->
    <div class="page-header">
      <img
        src="@/assets/images/logos/logo-text@2x.png"
        alt="Logo"
        class="header-logo"
      />
    </div>

    <div v-if="!isAuthenticated" class="auth-redirect">
      <SmartBanner />
    </div>

    <div class="profile-container">
      <div v-if="state.error" class="error-message">
        {{ state.error }}
      </div>

      <div v-if="loading" class="loading-container">
        <el-icon :size="50" class="loading-spinner">
          <Loading />
        </el-icon>
        <p class="mt-3">Loading profile...</p>
      </div>

      <template v-else-if="state.profile">
        <!-- Background with Avatar and Name on top -->
        <div 
          class="profile-header" 
          :style="{ backgroundImage: `url(${state.profile?.background || defaultBackground})` }"
        >
          <div class="profile-content-overlay">
            <div class="profile-avatar">
              <img :src="state.profile?.avatar || defaultAvatar" alt="Profile Avatar" />
            </div>
            <div class="profile-name-section">
              <h1 class="user-name">{{ fullName }}</h1>
            </div>
          </div>
        </div>

        <!-- Profile Content Section -->
        <div class="profile-content-container">
          <div class="profile-info-section">
            <!-- My Why Section -->
            <div class="bio-section" v-if="state.profile?.why">
              <h2 class="purpose-title">{{ state.profile.why }}</h2>
            </div>

            <div class="bio-section" v-if="state.profile?.bio">
              <h3>BIO</h3>
              <p>{{ state.profile.bio }}</p>
            </div>

            <!-- Values Section -->
            <div class="values-section" v-if="state.profile?.values?.length">
              <h3>VALUES</h3>
              <div class="tags-container">
                <el-tag
                  v-for="value in state.profile.values"
                  :key="value.id"
                  class="value-tag"
                  :class="{ favorite: value.favorite }"
                >
                  {{ value.value }}
                </el-tag>
              </div>
            </div>

            <!-- Interests Section -->
            <div class="interests-section" v-if="state.profile?.interests?.length">
              <h3>INTERESTS</h3>
              <div class="tags-container">
                <el-tag
                  v-for="interest in state.profile.interests"
                  :key="interest.id"
                  class="interest-tag"
                  :class="{ favorite: interest.favorite }"
                >
                  {{ interest.interest }}
                </el-tag>
              </div>
            </div>

            <!-- Bullhorns Section -->
            <div class="values-section" v-if="state.profile?.bullhorns?.length">
              <h3>CAUSES</h3>
              <div class="tags-container">
                <el-tag
                  v-for="bullhorn in state.profile.bullhorns"
                  :key="bullhorn.id"
                  class="interest-tag"
                >
                  {{ bullhorn.bullhorn }}
                </el-tag>
              </div>
            </div>

            <!-- Channels Section -->
            <div class="values-section" v-if="state.profile?.channels?.length">
              <h3>CHANNELS</h3>
              <div class="tags-container">
                <el-tag
                  v-for="channel in state.profile.channels"
                  :key="channel.id"
                  class="channel-tag"
                >
                  {{ channel.channel }}
                </el-tag>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </web-app-layout>
</template>

<script lang="ts">
import {
  defineComponent,
  onMounted,
  reactive,
  computed,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import WebAppLayout from '@/views/web-app/dashboard/WebAppLayout.vue';
import gqlRequest from '@/views/web-app/dashboard/gql-request';
import defaultAvatarImg from '@/assets/images/default-profile.jpeg';
import defaultBackgroundImg from '@/assets/images/bg.jpg';
import SmartBanner from '@/views/web-app/sticker/SmartBanner.vue';

interface ProfileData {
  profile_url?: string;
  id?: string;
  avatar?: string;
  background?: string;
  profile_qr_code?: string;
  bio?: string;
  why?: string;
  user?: {
    id?: string;
    firstname?: string;
    lastname?: string;
    title?: string;
    email?: string;
    phone?: string;
  };
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  values?: Array<{
    id: string;
    value: string;
    favorite: boolean;
  }>;
  interests?: Array<{
    id: string;
    interest: string;
    favorite: boolean;
  }>;
  bullhorns?: Array<{
    id: string;
    bullhorn: string;
  }>;
  channels?: Array<{
    id: string;
    channel: string;
  }>;
}

export default defineComponent({
  name: 'UserProfile',
  components: { WebAppLayout, SmartBanner },
  setup() {
    const {
      loading,
      fetchProfile,
      fetchPublicProfile,
      fetchPublicProfileBySlug,
    } = gqlRequest();
    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    const state = reactive({
      profile: null as ProfileData | null,
      error: null as string | null,
    });

    const defaultAvatar = defaultAvatarImg;
    const defaultBackground = defaultBackgroundImg;

    const currentUserId = computed(() => store.getters['users/me']?.id);
    const profileUserId = computed(() => route.params.id as string);
    const profileUserSlug = computed(() => route.params.slug as string);
    const isSlugRoute = computed(() => !!profileUserSlug.value);

    const isOwnProfile = computed(() => {
      if (!state.profile?.user?.id) return false;
      return currentUserId.value === state.profile.user.id;
    });

    const isAuthenticated = computed(() => store.getters['users/isMeSignedIn']);

    const fullName = computed(() => {
      if (!state.profile?.user) return 'Loading...';
      return `${state.profile.user.firstname || ''} ${state.profile.user.lastname || ''}`.trim() || 'No name set';
    });

    const hasAddress = computed(() => 
      state.profile?.address_line_1 || state.profile?.city || state.profile?.state,
    );

    const formattedAddress = computed(() => {
      if (!state.profile) return '';
      const parts: string[] = [];
      if (state.profile.address_line_1) parts.push(state.profile.address_line_1);
      if (state.profile.address_line_2) parts.push(state.profile.address_line_2);
      if (state.profile.city) parts.push(state.profile.city);
      if (state.profile.state) parts.push(state.profile.state);
      if (state.profile.zipcode) parts.push(state.profile.zipcode);
      return parts.join(', ');
    });

    async function loadProfile() {
      const identifier = profileUserId.value || profileUserSlug.value;

      // If no identifier is provided and user is authenticated, redirect to own profile
      if (!identifier && currentUserId.value) {
        router.push(`/web/profile/user/${currentUserId.value}`);
        return;
      }

      // If no identifier and not authenticated, show error
      if (!identifier) {
        state.error = 'Profile not found';
        return;
      }

      try {
        loading.value = true;
        state.error = null;
        let profile = null;

        // Handle slug-based routes
        if (isSlugRoute.value) {
          profile = await fetchPublicProfileBySlug(profileUserSlug.value);
        } else {
          // Handle ID-based routes
          if (isAuthenticated.value && currentUserId.value === profileUserId.value) {
            // If authenticated and viewing own profile, use fetchProfile for full data
            profile = await fetchProfile();
          } else {
            // For other users' profiles or unauthenticated users, use public profile
            profile = await fetchPublicProfile(profileUserId.value);
          }
        }

        if (profile) {
          state.profile = profile;
        } else {
          state.error = 'Profile not found';
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        state.error = 'Failed to load profile data';
        if (error.message?.includes('not authorized')) {
          router.push('/auth/web/signin');
        }
      } finally {
        loading.value = false;
      }
    }

    onMounted(() => {
      loadProfile();
    });

    watch(() => route.params.id, (newId) => {
      if (newId) {
        loadProfile();
      }
    });

    watch(() => route.params.slug, (newSlug) => {
      if (newSlug) {
        loadProfile();
      }
    });

    return {
      state,
      loading,
      defaultAvatar,
      defaultBackground,
      fullName,
      isAuthenticated,
      isOwnProfile,
    };
  },
});
</script>

<style lang="scss" scoped>
/* Page header styles */
.page-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 20px 0 10px 0;
  margin-bottom: 20px;
}

.header-logo {
  height: 28px;
  width: auto;
  max-width: 150px;
  object-fit: contain;
}

.profile-container {
  max-width: 900px;
  margin: 0;
  padding: 20px;
  text-align: left;
}

.profile-content-container {
  margin-top: 20px;
  text-align: left;
}

.profile-header {
  height: 300px;
  background-size: cover;
  background-position: center;
  position: relative;
  border-radius: 20px;
  margin-bottom: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  padding: 40px;
}

.profile-content-overlay {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 30px;
  width: 100%;
  padding-top: 100px;
}

.profile-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 4px solid white;
  overflow: hidden;
  background: #fff;
  box-shadow: none;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.profile-name-section {
  color: white;
  text-align: left;

  .user-name {
    color: white;
    font-size: 28px;
    font-weight: 700;
    margin: 0 0 10px 0;
    letter-spacing: -0.5px;
  }

  .user-title {
    color: rgba(255, 255, 255, 0.9);
    font-size: 16px;
    margin: 0;
    font-weight: 500;
  }
}

.profile-info-section {
  padding: 0 30px;
  background: transparent;
  border-radius: 0;
  box-shadow: none;

  @media (max-width: 768px) {
    padding: 0;
  }

  h3 {
    color: #2c3e50;
    font-size: 14px;
    margin: 35px 0 20px;
    text-align: left;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:first-child {
      margin-top: 0;
    }
  }
}

.bio-section {
  text-align: left;
  margin: 30px 0;
  padding: 0;
  background: transparent;
  border-radius: 0;

  &:first-child {
    margin-top: 0;
  }

  p {
    color: #4a5568;
    line-height: 1.8;
    margin: 0;
    font-size: 18px;
  }
}

.values-section, .interests-section {
  text-align: left;
  margin: 30px 0;

  &:first-child {
    margin-top: 0;
  }
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 15px 0;
  padding: 5px;
}

.value-tag, .interest-tag, .channel-tag {
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &.favorite {
    background-color: #005C6B !important;
    color: white !important;
    border: 1px solid #005C6B !important;
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0, 92, 107, 0.2);
  }

  &:not(.favorite) {
    background-color: transparent !important;
    color: #000000 !important;
    border: 1px solid #000000 !important;

    &:hover {
      transform: scale(1.05);
      background-color: #f5f5f5 !important;
    }
  }
}

/* Purpose title styling */
.purpose-title {
  color: #2c3e50;
  font-size: 24px;
  margin: 0 0 20px 0;
  text-align: left;
  font-weight: 700;
  line-height: 1.3;
}

.contact-section {
  text-align: left;
  margin: 30px 0;

  &:first-child {
    margin-top: 0;
  }

  h3 {
    color: #2c3e50;
    font-size: 14px;
    margin: 35px 0 20px;
    text-align: left;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:first-child {
      margin-top: 0;
    }
  }

  .contact-info {
    .info-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 10px 0;
      
      i {
        color: #666;
      }
      
      span {
        color: #4a5568;
        font-size: 16px;
      }
    }
  }
}

.loading-spinner {
  animation: rotate 2s linear infinite;
  color: #1fbeaf;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 20px;

  p {
    color: #5c6c7c;
    font-size: 18px;
  }
}

.auth-redirect {
  text-align: center;
  padding: 60px;
  font-size: 20px;
  color: #5c6c7c;
  background: #f8fafc;
  border-radius: 12px;
  margin: 20px;
}

.error-message {
  background-color: #fef2f2;
  color: #ef4444;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 25px;
  text-align: center;
  font-size: 16px;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.1);
}
</style>